import { observable, computed } from 'mobx';
import type { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import type Candidate from '@this/domain/appoint/candidate';
import { candidateTitle, candidateMobileTitle, travelTimeTitle } from '@this/domain/appoint/candidate';
import type OpenMeetingBlockedSlot from '@this/domain/appoint/open_meeting_blocked_slot';
import {
  blockedSlotTitle,
  blockedSlotMobileTitle,
  blockedSlotDisabledTitle
} from '@this/domain/appoint/open_meeting_blocked_slot';
import Calendar from './calendar';

export type EventResponse = {
  id?: string;
  adjustment_key?: string;
  adjustment_status?: string;
  name: string;
  start: string;
  end: string;
  calendar_id?: string;
  location?: string;
  description?: string;
  participants: {
    name?: string;
    connected_calendar_id: string;
    email: string;
    provider: string | null;
  }[];
  allday?: string;
  reminders?: string;
  meeting_type?: string;
  google_meet_url?: string;
  outlook_online_meeting_url?: string;
  zoom_meeting_join_url?: string;
};

export type EventArgs = {
  id?: string;
  adjustmentKey?: string;
  adjustmentStatus?: string;
  name: string;
  start: Moment;
  end: Moment;
  calendarId?: string;
  location?: string;
  description?: string;
  participants?: Calendar[];
  oldParticipants?: Calendar[];
  allday?: string;
  reminders?: string;
  meetingType?: string;
  googleMeetUrl?: string;
  outlookOnlineMeetingUrl?: string;
  zoomMeetingJoinUrl?: string;
};

export const convertEventResponseToArgs = (response: EventResponse) => {
  let start = moment(response.start);
  let end = moment(response.end);
  // 終日予定はJSTの0時から0時で渡ってくるため、テキストの状態で一度TZの情報を削除してユーザーのTZでmomentを生成
  if (response.start.indexOf('00:00:00') > -1 && response.end.indexOf('00:00:00') > -1) {
    const currentTz = moment.tz.guess();
    start = moment.tz(response.start.replace(/(Z|(\+|-)\d+:\d+)/, ''), currentTz);
    end = moment.tz(response.end.replace(/(Z|(\+|-)\d+:\d+)/, ''), currentTz);
  }
  const participants =
    response.participants?.map(
      p =>
        new Calendar({
          id: p.connected_calendar_id,
          auth_data_id: '',
          name: p.name || p.connected_calendar_id,
          email: p.email,
          provider: p.provider
        })
    ) || [];
  const args: EventArgs = {
    id: response.id,
    adjustmentKey: response.adjustment_key,
    adjustmentStatus: response.adjustment_status,
    name: response.name,
    start,
    end,
    calendarId: response.calendar_id,
    location: response.location,
    description: response.description,
    participants,
    oldParticipants: participants,
    allday: response.allday,
    reminders: response.reminders,
    meetingType: response.meeting_type,
    googleMeetUrl: response.google_meet_url,
    outlookOnlineMeetingUrl: response.outlook_online_meeting_url,
    zoomMeetingJoinUrl: response.zoom_meeting_join_url
  };
  return args;
};

export const convertCandidateToArgs = (c: Candidate, name?: string) => {
  const args: EventArgs = {
    name: name === undefined ? candidateTitle : name,
    start: c.start,
    end: c.end,
    calendarId: ''
  };
  return args;
};

export const convertTravelTimeToArgs = (c: Candidate) => {
  const args: EventArgs = {
    name: travelTimeTitle,
    start: c.start,
    end: c.end,
    calendarId: '',
    description: c.description
  };
  return args;
};

export const convertBlockedSlotToArgs = (s: OpenMeetingBlockedSlot, name?: string) => {
  const args: EventArgs = {
    name: name === undefined ? blockedSlotTitle : name,
    start: s.startAt,
    end: s.endAt,
    calendarId: ''
  };
  return args;
};

export default class Event {
  @observable id?: string;
  @observable adjustmentKey: string | null;
  @observable adjustmentStatus: string | null;
  @observable name: string;
  @observable start: Moment;
  @observable end: Moment;
  @observable calendarId?: string;
  @observable location?: string;
  @observable description?: string;
  @observable participants: Calendar[];
  @observable oldParticipants: Calendar[]; // participantsに変更があった場合用
  @observable allday?: string;
  @observable reminders?: string;
  @observable meetingType?: string;
  @observable googleMeetUrl?: string;
  @observable outlookOnlineMeetingUrl?: string;
  @observable zoomMeetingJoinUrl?: string;

  constructor(args: EventArgs) {
    this.id = args.id;
    this.adjustmentKey = args.adjustmentKey || null;
    this.adjustmentStatus = args.adjustmentStatus || null;
    this.name = args.name;
    this.start = args.start;
    this.end = args.end;
    this.calendarId = args.calendarId;
    this.location = args.location;
    this.description = args.description;
    this.participants = args.participants || [];
    this.oldParticipants = args.participants || [];
    this.allday = args.allday;
    this.reminders = args.reminders;
    this.meetingType = args.meetingType;
    this.googleMeetUrl = args.googleMeetUrl;
    this.outlookOnlineMeetingUrl = args.outlookOnlineMeetingUrl;
    this.zoomMeetingJoinUrl = args.zoomMeetingJoinUrl;
  }

  @computed
  get isCandidate() {
    return this.name === candidateTitle || this.name === candidateMobileTitle;
  }

  @computed
  get isBlockedSlot() {
    return [blockedSlotTitle, blockedSlotMobileTitle, blockedSlotDisabledTitle].indexOf(this.name) > -1;
  }

  submitParams() {
    return {
      event_id: this.id,
      name: this.name,
      start: this.start.format('YYYY-MM-DD HH:mm'),
      end: this.end.format('YYYY-MM-DD HH:mm'),
      location: this.location,
      participants: this.participants.map(p =>
        p ? { id: p.id, provider: p.provider, auth_data_id: p.authDataId } : null
      ),
      old_participants: this.oldParticipants.map(p =>
        p ? { id: p.id, provider: p.provider, auth_data_id: p.authDataId } : null
      ),
      description: this.description,
      meetingType: this.meetingType
    };
  }

  deleteParams() {
    return {
      event_id: this.id,
      calendar_id: this.calendarId,
      old_participants: this.oldParticipants.map(p =>
        p ? { id: p.id, provider: p.provider, auth_data_id: p.authDataId } : null
      )
    };
  }
}
